import React from "react"
import styled from "styled-components"

import { MedicationСardHeader, MedicationСardHeaderProps } from "./header"
import { MedicationСardBody } from "./body"
import { MedicationСardFooter } from "./footer"

import { Flex } from "components/box"

import { Color } from "theme"

type MedicationСardProps = {
  categoryColor: Color
  description?: string
  price?: string
  onClick?: (event?: React.MouseEvent) => void
  ariaLabel?: string
  needsPrescription?: boolean
} & MedicationСardHeaderProps

const hex2rgba = (hex: string, alpha = 1) => {
  const hexMatch = hex.match(/\w\w/g)
  const [r, g, b] = hexMatch?.map(x => parseInt(x, 16))
  return `rgba(${r},${g},${b},${alpha})`
}

const Container = styled(Flex).attrs({
  py: 4,
  pl: 4,
  pr: 3
})<{ categoryColor: Color }>`
  height: 7.4375rem;
  border-radius: 0.3125rem;
  box-shadow: 0px 0.0625rem 0.25rem rgba(215, 220, 218, 0.5);
  background-color: ${props => props.theme.colors.iceBlue};
  transition: all 0.4s ease-out;

  &:hover,
  &:focus {
    background-color: ${props => hex2rgba(props.theme.colors[props.categoryColor], 0.5)};
    box-shadow: none;
    cursor: pointer;
    transition: all 0.3s ease-out;
    outline: none;
  }

  &:active {
    background-color: ${props => hex2rgba(props.theme.colors[props.categoryColor], 0.75)};
  }

  svg rect {
    fill: ${props => props.theme.colors[props.categoryColor]};
  }
`

const MedicationСard: React.FC<MedicationСardProps> = ({
  name,
  price,
  categoryColor,
  onClick,
  description,
  ariaLabel,
  needsPrescription = false,
  isNewProduct = false
}) => {
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        onClick && onClick()
      }
    },
    [onClick]
  )

  return (
    <Container
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={event => handleKeyDown(event)}
      flexDirection="column"
      categoryColor={categoryColor}
    >
      <MedicationСardHeader name={name} isNewProduct={isNewProduct} />
      {description && <MedicationСardBody description={description} />}
      {price && <MedicationСardFooter price={price} needsPrescription={needsPrescription} />}
    </Container>
  )
}

export default MedicationСard
